<template>
	<V2Layout>
		<div class="wiz">

			<!-- Progress Bar -->
			<div class="wiz__progress">
				<template v-for="(s, idx) in wizardSteps">
					<div
						v-if="idx > 0"
						:key="'c-'+idx"
						class="wiz__connector"
						:class="{
							'wiz__connector--done': step > idx - 1
						}"
					/>

					<div
						:key="'s-'+idx"
						class="wiz__step"
						:class="{
							'wiz__step--active': step === idx,
							'wiz__step--done': step > idx,
							'wiz__step--future': step < idx
						}"
						@click="step > idx && (step = idx)"
					>
						<span class="wiz__circle">
							<svg v-if="step > idx" width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M3 7L6 10L11 4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
							<span v-else>{{ idx + 1 }}</span>
						</span>
						<span class="wiz__label">{{ s.label }}</span>
					</div>
				</template>
			</div>

			<!-- Page Title -->
			<div class="wiz__header">
				<h1 class="wiz__title">Create Campaign</h1>
				<p class="wiz__subtitle">{{ wizardSteps[step] && wizardSteps[step].sub }}</p>
			</div>

			<!-- ═══════════════════════════════════════ -->
			<!-- STEP 1: Setup (Basics + Strategy)      -->
			<!-- ═══════════════════════════════════════ -->
			<div
				v-show="step === 0"
				class="wiz__setup"
			>
				<div class="wiz__setup-left">
					<div class="wiz__card">
						<div class="wiz__card-accent wiz__card-accent--teal" />
						<h3 class="wiz__card-title">Campaign Information</h3>
						<step01
							v-model="valueStep01"
							:context="{ instance, step: 0, errors }"
							:step-current="0"
							:step-count="wizardSteps.length"
							:go-to-next="noOp"
							:go-to-prev="noOp"
						/>
					</div>
				</div>

				<div class="wiz__setup-right">
					<h3 class="wiz__section-label">Campaign Strategy</h3>

					<!-- Auction Card -->
					<div
						class="wiz__strategy"
						:class="{
							'wiz__strategy--selected': !isGuaranteed
						}"
						@click="selectAuction"
					>
						<div class="wiz__strategy-accent wiz__strategy-accent--teal" />

						<div class="wiz__strategy-icon wiz__strategy-icon--teal">
							<svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M7 14L15 8L7 2V14Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>
						</div>

						<div class="wiz__strategy-body">
							<div class="wiz__strategy-title">Auction Campaign</div>
							<div class="wiz__strategy-desc">Compete for screen time through real-time bidding. Your ads are shown based on your bid competing with other advertisers.</div>
						</div>

						<div
							class="wiz__strategy-radio"
							:class="{
								'wiz__strategy-radio--on': !isGuaranteed
							}"
						/>
					</div>

					<!-- Pricing pills (visible when auction selected) -->
					<div v-show="!isGuaranteed" class="wiz__pricing">
						<step02
							:context="{ instance, step: 0, errors }"
							:step-current="0"
							:step-count="wizardSteps.length"
							:go-to-next="noOp"
							:go-to-prev="noOp"
							@payment-model-change="handlePaymentModelChange"
						/>
					</div>

					<!-- Guaranteed Card -->
					<div
						class="wiz__strategy wiz__strategy--purple"
						:class="{
							'wiz__strategy--selected-purple': isGuaranteed
						}"
						@click="selectGuaranteed"
					>
						<div class="wiz__strategy-accent wiz__strategy-accent--purple" />

						<div class="wiz__strategy-icon wiz__strategy-icon--purple">
							<svg width="20" height="20" viewBox="0 0 20 20" fill="none">
								<path d="M10 2L4 4.5V10C4 13.5 6.5 16.5 10 17.5C13.5 16.5 16 13.5 16 10V4.5L10 2Z" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/>
								<path d="M7.5 10L9.5 12L12.5 8.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
							</svg>
						</div>

						<div class="wiz__strategy-body">
							<div class="wiz__strategy-title">Guaranteed Campaign</div>
							<div class="wiz__strategy-desc">Fixed placement on selected screens with guaranteed delivery and priority scheduling. Your ads are shown at specific times without competition.</div>
						</div>

						<div
							class="wiz__strategy-radio"
							:class="{
								'wiz__strategy-radio--on wiz__strategy-radio--purple': isGuaranteed
							}"
						/>
					</div>

					<!-- Purple info note (replaces the old "Coming Soon" block) -->
					<div
						v-if="isGuaranteed"
						class="wiz__info-note"
					>
						<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
						<span>{{ $t('guaranteed.strategy_info_note') }}</span>
					</div>
				</div>
			</div>

			<!-- ═══════════════════════════════════════ -->
			<!-- STEP 2: Targeting & Budget              -->
			<!-- ═══════════════════════════════════════ -->
			<div v-show="step === 1" class="wiz__targeting">
				<step03
					v-model="valueStep03"
					:context="{ instance, step: 1, errors }"
					:step-current="1"
					:step-count="wizardSteps.length"
					:go-to-next="noOp"
					:go-to-prev="noOp"
					@update:value="debouncedCreateOrUpdateRequest"
				/>
			</div>

			<!-- ═══════════════════════════════════════ -->
			<!-- STEP 3: Creatives                      -->
			<!-- ═══════════════════════════════════════ -->
			<div v-show="step === 2" class="wiz__creatives">
				<step04
					v-model="valueStep04"
					:context="{ instance, step: 2, errors }"
					:step-current="2"
					:step-count="wizardSteps.length"
					:go-to-next="noOp"
					:go-to-prev="noOp"
					@request-create="createRequest"
				/>
			</div>

			<!-- Footer Navigation -->
			<div class="wiz__footer">
				<button v-if="step > 0" class="wiz__btn wiz__btn--outline" @click="prevStep">&larr; Back</button>
				<button v-else class="wiz__btn wiz__btn--ghost" @click="backButton">&larr; Back to Requests</button>

				<div class="wiz__footer-right">
					<template v-if="step === wizardSteps.length - 1">
						<button class="wiz__btn wiz__btn--outline" @click="createRequest({ doPlaceOrder: false, finish: true })">Save Draft</button>
						<button class="wiz__btn wiz__btn--success" @click="createRequest({ doPlaceOrder: true, finish: true })">Place Order &rarr;</button>
					</template>
					<button v-else class="wiz__btn wiz__btn--primary" @click="nextStepAction">Next &rarr;</button>
				</div>
			</div>

			<!-- SVG defs for step02 payment icons -->
			<svg xmlns="http://www.w3.org/2000/svg" style="display:none">
				<defs>
					<symbol id="payment_model_icon__ad_plays" viewBox="0 0 78 79"><circle opacity="0.1" cx="39" cy="39" r="39" fill="currentColor"/><path d="M39.7 28H49.7C53 28 55.7 30.7 55.7 34V44C55.7 47.3 53 50 49.7 50H39.7V28Z" fill="currentColor"/></symbol>
					<symbol id="payment_model_icon__cpm" viewBox="0 0 78 78"><circle opacity="0.1" cx="39" cy="39" r="39" fill="currentColor"/><path d="M40.1 21.7C32.9 21.7 26.9 27.3 26.2 34.4L21.5 43.7H26.1V46.7C26.1 49.5 28.4 51.7 31.1 51.7H34.1V54.7C34.1 55.3 34.6 55.7 35.1 55.7C35.7 55.7 36.1 55.3 36.1 54.7V49.7H31.1C29.5 49.7 28.1 48.4 28.1 46.7V41.7H24.8L28.2 34.9C28.7 28.6 33.9 23.7 40.1 23.7C46.8 23.7 52.1 29.1 52.1 35.7C52.1 38.6 51.2 41.2 49.5 43.2C48.7 44.2 48.1 45.4 48.1 46.8V54.7C48.1 55.3 48.6 55.7 49.1 55.7C49.7 55.7 50.1 55.3 50.1 54.7V46.8C50.1 46 50.5 45.2 51.1 44.5C53 42.1 54.1 39 54.1 35.7C54.1 28 47.9 21.7 40.1 21.7Z" fill="currentColor"/></symbol>
					<symbol id="payment_model_icon__cpv" viewBox="0 0 78 78"><circle opacity="0.1" cx="39" cy="39" r="39" fill="currentColor"/><path d="M20 38.6C24.1 32.2 31 28 39 28C47 28 53.9 32.2 58 38.6L58.3 38.8C58.3 38.9 58.3 38.9 58.2 39C58.3 39.1 58.3 39.1 58.3 39.2L58 39.4C53.9 45.8 47 50 39 50C31 50 24.1 45.8 20 39.4Z" fill="currentColor"/></symbol>
				</defs>
			</svg>

			<errors-modal v-if="showErrorsPopup" :errors="errors" @close="showErrorsPopup = false" />
		</div>
	</V2Layout>
</template>


<script>
import _ from 'underscore';

import {
	PAYMENT_MODEL_AD_PLAYS,
	PAYMENT_MODEL_GUARANTEED,
} from '@/constants/payment_models';
import V2Layout from '@/layouts/V2Layout';
import OriginalWizard from '@/ui-overrides/default/pages/request/create/index.vue';

import V2StepTargeting from './create-step-targeting.vue';
import V2StepCreatives from './create-step-creatives.vue';


export default {
	name: 'V2RequestCreate',
	extends: OriginalWizard,

	components: {
		...OriginalWizard.components,
		V2Layout,
		step03: V2StepTargeting,
		step04: V2StepCreatives,
	},

	data() {
		return {
			...OriginalWizard.data.call(this),
		};
	},

	mounted() {
		if (this.$route.hash) {
			const stepNum = parseInt(this.$route.hash.replace('#step', ''));
			if (stepNum > 0) {
				this.step = stepNum;
			}
		}

		// TODO: refactor and/or disable listeners on `beforeDestroy`
		// Delegate clicks on payment-model cards to their hidden Select button
		this.$el.addEventListener('click', (e) => {
			const card = e.target.closest('.payment-model');
			if (card) {
				const btn = card.querySelector('.payment-model__select-button');
				if (btn && e.target !== btn && !btn.contains(e.target)) {
					btn.click();
				}
			}
		});
	},

	beforeRouteLeave(to, from, next) {
		if (to.name === 'advertiser-add' || to.name === 'v2-advertiser-add') {
			sessionStorage.setItem('v2WizardReturnUrl', from.fullPath);
		}
		next();
	},

	computed: {
		wizardSteps() {
			return [
				{ slug: 'setup', label: 'Setup', sub: 'Set up your campaign details and choose a strategy.' },
				{ slug: 'targeting', label: 'Targeting', sub: 'Configure where, when, and how much for your campaign.' },
				{ slug: 'creatives', label: 'Creatives', sub: 'Upload your ad content and submit.' },
			];
		},

		currentStepSlug() {
			return this.wizardSteps[this.step] ? this.wizardSteps[this.step].slug : 'setup';
		},

		isGuaranteed() {
			return this.valueStep03.payment_model === PAYMENT_MODEL_GUARANTEED;
		},

		// we need to duplicate it here otherwise save is broken
		// NB: copy from `ui-overrides/default/pages/request/create/index.vue`
		instance: {
			get() {
				let start_date = null;
				let end_date = null;

				if (this.valueStep03.time_period) {
					start_date = this.valueStep03.time_period.start;
					end_date = this.valueStep03.time_period.end;
				}

				// convert to float
				// TODO: WHY? and limit the number of decimal places
				let max_bid = 0;
				if (this.valueStep03.max_bid) {
					// max_bid = parseFloat(parseFloat(this.valueStep03.max_bid).toFixed(2));
					max_bid = parseFloat(this.valueStep03.max_bid);
				}

				let ad_rate_per_hour = null;
				if (this.valueStep03.ad_rate_per_hour) {
					ad_rate_per_hour = this.valueStep03.ad_rate_per_hour * this.valueStep03.ad_rate_multiplier;
				}

				// TODO: we need a better default values system
				let payment_model = this.enabledPaymentModels[0];
				if (this.valueStep03.payment_model) {
					payment_model = this.valueStep03.payment_model;
				}
				// set `payment_model` if `enabledPaymentModels.length === 1`
				if (this.enabledPaymentModels.length === 1) {
					payment_model = this.enabledPaymentModels[0];
				}

				return {
					...this.valueStep01,
					...this.valueStep03,
					payment_model,
					end_date,
					max_bid,
					start_date,
					ad_rate_per_hour,

					...this.valueStep04,
				};
			},

			set(instance) {
				this.valueStep = {...instance};

				this.valueStep01 = {
					..._.pick(instance, [
						'id',
						'name',
						'industry',
						'description',
						'brand',
						'brand_instance',
						'advertiser',
						'advertiser_instance',
					]),
				};

				// this.valueStep02 = {
				// 	..._.pick(instance, [
				// 		'payment_model'
				// 	]),
				// };

				this.valueStep03 = {
					..._.pick(instance, [
						'areas',
						'platforms',
						'age',
						'gender',
						'interests',
						'life_stages',
						'intents',
						'environments',
						'pois',
						'cities',
						'resolutions',
						'orientations',
						'device_types',
						//'time_period', // see below
						'start_date',
						'end_date',
						// guaranteed
						'start_time',
						// guaranteed
						'end_time',
						'time_range',
						'schedule',
						'ad_duration',
						'ad_rate_multiplier',
						'ad_rate_per_hour',
						'payment_model',
						'budget',
						'daily_budget',
						'is_fixed_placement',
						'is_budget_manually_set',
						'is_max_bid_manually_set',
						'max_bid',
						'owner_instance',
						'timing_rules',
						'book_without_content',
						'content_show_count',
						'playback_order',
					]),
				};

				// set `payment_model` if `enabledPaymentModels.length === 1`
				if (this.enabledPaymentModels.length === 1) {
					this.valueStep03.payment_model = this.enabledPaymentModels[0];
				}

				this.valueStep03.time_period = {
					start: instance.start_date,
					end: instance.end_date,
				};

				this.valueStep03.ad_rate_per_hour = this.valueStep03.ad_rate_per_hour / this.valueStep03.ad_rate_multiplier;

				// fix `budget` & `max_bid` - remove 0 and null values
				// and prevent overwrite
				this.valueStep03.budget = parseFloat(this.valueStep03.budget);
				if (!this.valueStep03.budget) {
					delete this.valueStep03.budget;
				}
				this.valueStep03.max_bid = parseFloat(this.valueStep03.max_bid);
				if (!this.valueStep03.max_bid) {
					delete this.valueStep03.max_bid;
				}
				this.valueStep03.daily_budget = parseFloat(this.valueStep03.daily_budget);

				// set default currency
				this.valueStep03.currency = instance.currency || this.agencyCurrency;

				this.valueStep04 = {
					..._.pick(instance, [
						'ads'
					]),
				};
			},
		}, // END: instance
	}, // END: computed

	methods: {
		noOp() {},

		selectGuaranteed() {
			OriginalWizard.methods.handlePaymentModelChange.call(this, PAYMENT_MODEL_GUARANTEED);
		},

		selectAuction() {
			OriginalWizard.methods.handlePaymentModelChange.call(this, PAYMENT_MODEL_AD_PLAYS);
		},

		async nextStepAction() {
			try {
				await this.v2NextStep();

				if (this.isGuaranteed && this.valueStep03.book_without_content && this.step === this.wizardSteps.length - 2) {
					// Skip Step 3 (Creatives) when the user reserved airtime without content.
					await this.createRequest({ doPlaceOrder: true, finish: true });
					return;
				}

				if (this.step < this.wizardSteps.length - 1) {
					this.step++;
					this.$router.push({ hash: `step${this.step}`});
				}
			} catch (err) {
				// TODO: validation failed
			}
		},

		async v2NextStep() {
			const instance = await OriginalWizard.methods.createOrUpdateRequest.call(this);

			let isError = Object.keys(this.errors).length > 0;
			if (isError) {
				this.showErrorsPopup = true;
				throw Error('validation');
			}

			if (
				this.step === 0
				&& this.$route.name === 'v2-request-add'
				&& instance
				&& instance.id
			) {
				window.history.replaceState({}, '', `/v2/request/${instance.id}#step1`);
			}

			return instance;
		},

		prevStep() {
			if (this.step > 0) {
				this.step--;
				this.$router.push({ hash: `step${this.step}`});
			}
		},

		backButton() {
			this.$router.push({ name: 'v2-request-list' });
		},

		modalClose() {
			this.$router.push({ name: 'v2-request-list' });
		},
	},
};
</script>


<style lang="sass" scoped>
@import '~@/assets/styles/v2-tokens.sass'

$teal: $v2-teal
$purple: $v2-purple
$green: $v2-green
$border: $v2-border
$card: $v2-card-bg
$text: $v2-text
$muted: $v2-text-muted
$label: $v2-text-label
$bg: #f3f4f6
$font: $v2-font
$r-card: 16px
$r-input: 10px
$shadow: 0 1px 3px rgba(0,0,0,0.08)

.wiz
	font-family: $font
	max-width: 1100px

// ── Progress ────────────────────────
.wiz__progress
	display: flex
	align-items: center
	justify-content: center
	padding: 16px 0 20px

.wiz__step
	display: flex
	align-items: center
	gap: 8px
	cursor: default

	&--done
		cursor: pointer

.wiz__circle
	width: 32px
	height: 32px
	border-radius: 50%
	display: flex
	align-items: center
	justify-content: center
	font-size: 13px
	font-weight: 700
	border: 2px solid $border
	color: $muted
	background: $card
	transition: all 0.2s

	.wiz__step--active &
		background: $teal
		border-color: $teal
		color: #fff

	.wiz__step--done &
		background: $teal
		border-color: $teal
		color: #fff

.wiz__label
	font-size: 13px
	font-weight: 500
	color: $muted

	.wiz__step--active &
		color: $text
		font-weight: 600

.wiz__connector
	width: 48px
	height: 2px
	background: $border
	margin: 0 12px
	border-radius: 1px

	&--done
		background: $teal

// ── Header ──────────────────────────
.wiz__header
	margin-bottom: 20px

.wiz__title
	font-size: 22px
	font-weight: 700
	color: $text
	margin: 0

.wiz__subtitle
	font-size: 13px
	color: $muted
	margin: 4px 0 0

// ── Step 1: Setup layout ────────────
.wiz__setup
	display: grid
	grid-template-columns: 1fr 1fr
	gap: 20px
	align-items: start

	@media (max-width: 768px)
		grid-template-columns: 1fr

.wiz__setup-left,
.wiz__setup-right
	min-width: 0

// ── Card with accent bar ────────────
.wiz__card
	background: $card
	border: 1px solid $border
	border-radius: $r-card
	padding: 20px 24px
	box-shadow: $shadow
	position: relative
	overflow: hidden

.wiz__card-accent
	position: absolute
	left: 0
	top: 0
	bottom: 0
	width: 4px

	&--teal
		background: $teal

	&--purple
		background: $purple

.wiz__card-title
	font-size: 14px
	font-weight: 700
	color: $text
	margin: 0 0 16px
	padding-left: 8px

// ── Section label ───────────────────
.wiz__section-label
	font-size: 13px
	font-weight: 700
	color: $text
	margin: 0 0 12px

// ── Strategy cards ──────────────────
.wiz__strategy
	display: flex
	gap: 14px
	padding: 18px 20px
	background: $card
	border: 2px solid $border
	border-radius: $r-card
	cursor: pointer
	transition: all 0.2s
	position: relative
	overflow: hidden
	margin-bottom: 12px

	&:hover
		box-shadow: 0 4px 12px rgba(0,0,0,0.08)

	&--selected
		border-color: $teal
		background: rgba($teal, 0.04)

	&--selected-purple
		border-color: $purple
		background: rgba($purple, 0.04)

.wiz__strategy-accent
	position: absolute
	left: 0
	top: 0
	bottom: 0
	width: 4px

	&--teal
		background: $teal

	&--purple
		background: $purple

.wiz__strategy-icon
	width: 40px
	height: 40px
	border-radius: 10px
	display: flex
	align-items: center
	justify-content: center
	flex-shrink: 0

	&--teal
		background: rgba($teal, 0.1)
		color: $teal

	&--purple
		background: rgba($purple, 0.1)
		color: $purple

.wiz__strategy-body
	flex: 1
	min-width: 0

.wiz__strategy-title
	font-size: 15px
	font-weight: 700
	color: $text
	margin-bottom: 4px

.wiz__strategy-desc
	font-size: 13px
	color: $muted
	line-height: 1.5

.wiz__strategy-radio
	width: 20px
	height: 20px
	border: 2px solid $border
	border-radius: 50%
	flex-shrink: 0
	display: flex
	align-items: center
	justify-content: center
	transition: all 0.15s
	align-self: flex-start
	margin-top: 2px

	&--on
		border-color: $teal

		&::after
			content: ''
			width: 10px
			height: 10px
			border-radius: 50%
			background: $teal

	&--purple
		border-color: $purple

		&::after
			background: $purple

// ── Badge ───────────────────────────
.wiz__badge
	display: inline-flex
	padding: 2px 8px
	border-radius: 9999px
	font-size: 10px
	font-weight: 600
	text-transform: uppercase
	letter-spacing: 0.04em
	vertical-align: middle
	margin-left: 6px

	&--purple
		background: rgba($purple, 0.1)
		color: $purple

// ── Strategy info note (purple, replaces old "Coming Soon") ──
.wiz__info-note
	display: flex
	align-items: flex-start
	gap: 10px
	padding: 12px 14px
	background: rgba($purple, 0.06)
	border: 1px solid rgba($purple, 0.2)
	border-radius: $r-input
	font-size: 13px
	color: $purple
	line-height: 1.5
	font-weight: 500
	margin-top: 4px

	svg
		flex-shrink: 0
		margin-top: 1px

// ── Pricing pills (step02 overrides) ─
.wiz__pricing
	margin-bottom: 12px

.wiz__pricing ::v-deep

	.campaign-form_model-select
		padding: 0 !important
		margin: 0 !important

	.welcome-message
		display: none !important

	.card-list
		display: flex !important
		gap: 8px !important

	.payment-model
		flex: 1 !important
		display: flex !important
		flex-direction: column !important
		align-items: center !important
		gap: 2px !important
		padding: 12px 8px !important
		border: 2px solid $border !important
		border-radius: $r-input !important
		text-align: center !important
		cursor: pointer !important
		transition: all 0.2s !important
		width: auto !important

		&:hover
			border-color: rgba($teal, 0.4) !important
			background: rgba($teal, 0.04) !important

		&.payment-model_active
			border-color: $teal !important
			background: rgba($teal, 0.06) !important
			box-shadow: 0 0 0 1px $teal !important

	.payment-model__icon
		width: 0 !important
		height: 0 !important
		display: none !important

	.payment-model__title
		font-size: 13px !important
		font-weight: 600 !important
		color: $text !important
		margin: 0 !important

	.payment-model__goal-label
		display: none !important

	.payment-model__goal
		font-size: 11px !important
		color: $muted !important
		margin: 0 !important

	.payment-model__description
		display: none !important

	.payment-model__select-button
		position: absolute !important
		opacity: 0 !important
		pointer-events: none !important
		height: 0 !important


// ── Step overrides (targeting + creatives + card) ─
.wiz__targeting ::v-deep,
.wiz__creatives ::v-deep,
.wiz__card ::v-deep

	.layout-full_width,
	.layout-center805
		margin: 0 !important
		padding: 0 !important
		max-width: none !important

		> *
			margin-left: 0 !important
			margin-right: 0 !important
			max-width: 100% !important

	// Hide original buttons
	.control-row > .nice-button,
	.ni_wizard_2--tabs,
	.ni_wizard_2--navigation,
	.header_panel-control,
	.advanced-settings-switch
		display: none !important

	// Show advanced sections
	.model-select-line[style],
	.data_block[style],
	.device-choices[style],
	.duration-n-rate[style],
	.fixed-placement[style]
		display: block !important

	.control-row__message
		font-size: 12px
		color: $v2-red

	// Inputs
	.nice-input .ni_input--input
		border: 1px solid $border !important
		border-radius: $r-input !important
		padding: 10px 14px !important
		background: #f8f9fb !important
		font-size: 14px !important

		&:focus
			border-color: $teal !important
			background: #fff !important
			box-shadow: 0 0 0 3px rgba($teal, 0.12) !important

	.nice-select .ni_select--select-row
		border: 1px solid $border !important
		border-radius: $r-input !important
		padding: 8px 14px !important
		background: #f8f9fb !important

	.ni_select--button_dd
		min-height: auto !important
		padding: 0 !important
		border: none !important
		background: transparent !important

	.nice-label
		font-family: $font !important

	.nice-button
		font-family: $font !important
		border-radius: $r-input !important

	// Map
	.map-container,
	.mapboxgl-map
		border-radius: 12px
		overflow: hidden

// ── Footer ──────────────────────────
.wiz__footer
	display: flex
	justify-content: space-between
	align-items: center
	margin-top: 20px

.wiz__footer-right
	display: flex
	gap: 10px

// ── Buttons ─────────────────────────
.wiz__btn
	display: inline-flex
	align-items: center
	gap: 6px
	padding: 10px 24px
	border-radius: $r-input
	font-family: $font
	font-size: 13px
	font-weight: 600
	cursor: pointer
	transition: all 0.15s
	border: none

	&--primary
		background: $teal
		color: #fff
		&:hover
			background: darken($teal, 8%)
		&:disabled
			opacity: 0.5
			cursor: not-allowed
			&:hover
				background: $teal

	&--success
		background: $green
		color: #fff
		&:hover
			background: darken($green, 8%)

	&--outline
		background: transparent
		border: 1.5px solid $border
		color: $muted
		&:hover
			border-color: $muted
			color: $text

	&--ghost
		background: transparent
		color: $muted
		&:hover
			color: $teal
</style>
